import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { environment } from '../../../../environment/environment';
import toast from '../../../../utils/toast';

export interface CompromisedEmployerItem {
  id: number;
  name: string;
  is_compromised: boolean;
  is_compromised_reason: string;
  is_compromised_date_updated: string;
}

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CompromisedEmployerItem[];
}

const PAGE_SIZE = 25;

export enum SortOrder {
  Asc = 'asc',
  Desc = 'desc'
}

export const useCompromisedEmployers = (
  searchQuery: string,
  page: number,
  activeOnly?: boolean,
  sortBy?: string,
  sortOrder?: SortOrder
) => {
  const [data, setData] = useState<CompromisedEmployerItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(0);
  const latestRequestId = useRef(0);

  const fetchCompromisedEmployers = useCallback(async () => {
    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    setLoading(true);
    try {
      const response = await axios.get<ApiResponse>(`${environment.baseUrl}/employer/compromised-list/`, {
        params: {
          page,
          page_size: PAGE_SIZE,
          search: searchQuery || undefined,
          active_only: activeOnly,
          sort_by: sortBy || undefined,
          sort_order: sortOrder || undefined,
        },
      });
      if (requestId !== latestRequestId.current) return;
      setData(response.data.results);
      setTotalPages(Math.ceil(response.data.count / PAGE_SIZE));
    } catch {
      if (requestId !== latestRequestId.current) return;
      toast.error('Failed to fetch compromised employers');
      setData([]);
      setTotalPages(0);
    } finally {
      if (requestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  }, [page, searchQuery, activeOnly, sortBy, sortOrder]);

  useEffect(() => {
    fetchCompromisedEmployers();
  }, [fetchCompromisedEmployers]);

  return { data, loading, totalPages, refetch: fetchCompromisedEmployers };
};
